import React, { forwardRef, memo, useCallback } from 'react';
import InputLabel, { IInputLabelProps } from '../connectedForm/InputLabel';
import InputBlock from '../connectedForm/InputBlock';
import { Input, InputProps, makeStyles } from '@fluentui/react-components';
import { Maybe } from 'graphql/jsutils/Maybe';
import InputGroup from '../connectedForm/InputGroup';

export const FluentInputTypesMap = {
  Number: 'number',
  Search: 'search',
  Text: 'text',
  Time: 'time',
  Tel: 'tel',
  URL: 'url',
  Email: 'email',
  Date: 'date',
  DateTimeLocal: 'datetime-local',
  Month: 'month',
  Password: 'password',
  Week: 'week',
} as const;

export type FluentInputTypes = typeof FluentInputTypesMap;

export interface IFluentInputProps extends Omit<InputProps, 'onChange' | 'type'> {
  type: keyof FluentInputTypes;
  value: any;
  defaultValue: any;
  label?: string;
  labelProportion?: number;
  onChange?: (value: Maybe<string>) => void;
  readOnly?: boolean;
  labelProps?: IInputLabelProps;
}

const useStyles = makeStyles({
  field: {
    transform: 'translateY(-5px)',
  },
});

const FluentInput = memo<IFluentInputProps>(
  forwardRef<HTMLInputElement, IFluentInputProps>(
    ({ label, value, labelProportion = 1 / 4, labelProps, type, onChange, readOnly, ...restProps }, ref) => {
      const styles = useStyles();

      const onChangeHandler = useCallback(
        (inputValue: string) => {
          if (!onChange) return;

          onChange(inputValue);
        },
        [onChange]
      );

      return (
        <InputBlock flexBox={true}>
          <InputLabel {...labelProps} cols={labelProportion} label={label} />
          <InputGroup flexBox={true} cols={label ? 1 - labelProportion : 1} withLabel={!!label}>
            <div className={styles.field}>
              <Input
                ref={ref}
                type={FluentInputTypesMap[type]}
                value={value}
                onChange={(_, value) => {
                  onChangeHandler(value.value);
                }}
                appearance={'underline'}
                disabled={readOnly}
                {...restProps}
              />
            </div>
          </InputGroup>
        </InputBlock>
      );
    }
  )
);

export default FluentInput;
